import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import type { WizardState } from "./OnboardingWizard";

interface Props {
  invites: WizardState["teamInvites"];
  onInvite: (invites: WizardState["teamInvites"]) => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
}

interface InviteRow {
  email: string;
  name: string;
  role: string;
}

const EMPTY_ROW: InviteRow = { email: "", name: "", role: "recruiter" };

const Step3InviteTeam: React.FC<Props> = ({
  invites,
  onInvite,
  onBack,
  isLoading,
}) => {
  const { t } = useTranslation("auth");
  const [rows, setRows] = useState<InviteRow[]>(
    invites.length > 0 ? invites : [],
  );

  const addRow = () => {
    if (rows.length >= 20) return;
    setRows([...rows, { ...EMPTY_ROW }]);
  };

  const removeRow = (index: number) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof InviteRow, value: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const validRows = rows.filter(
    (r) => r.email.trim() !== "" && r.name.trim() !== "",
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onInvite(validRows);
  };

  const handleSkip = async () => {
    await onInvite([]);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="wizard-step-content">
        <h2>{t("onboarding.inviteTeam.title")}</h2>
        <p className="step-description">
          {t("onboarding.inviteTeam.description")}
        </p>

        {rows.length > 0 && (
          <table className="invite-table">
            <thead>
              <tr>
                <th>{t("onboarding.inviteTeam.table.email")}</th>
                <th>{t("onboarding.inviteTeam.table.name")}</th>
                <th>{t("onboarding.inviteTeam.table.role")}</th>
                <th style={{ width: 40 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>
                    <input
                      type="email"
                      value={row.email}
                      onChange={(e) => updateRow(i, "email", e.target.value)}
                      placeholder={t("onboarding.inviteTeam.emailPlaceholder")}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={row.name}
                      onChange={(e) => updateRow(i, "name", e.target.value)}
                      placeholder={t("onboarding.inviteTeam.namePlaceholder")}
                    />
                  </td>
                  <td>
                    <select
                      value={row.role}
                      onChange={(e) => updateRow(i, "role", e.target.value)}
                    >
                      <option value="recruiter">
                        {t("onboarding.inviteTeam.roles.recruiter")}
                      </option>
                      <option value="sales">
                        {t("onboarding.inviteTeam.roles.sales")}
                      </option>
                      <option value="organization_admin">
                        {t("onboarding.inviteTeam.roles.organizationAdmin")}
                      </option>
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-remove"
                      onClick={() => removeRow(i)}
                      title={t("onboarding.inviteTeam.table.remove")}
                    >
                      <X size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {rows.length < 20 && (
          <button type="button" className="btn-add-row" onClick={addRow}>
            <Plus size={16} /> {t("onboarding.inviteTeam.addMember")}
          </button>
        )}
      </div>

      <div className="wizard-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onBack}
          disabled={isLoading}
        >
          {t("onboarding.inviteTeam.actions.back")}
        </button>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          {rows.length === 0 ? (
            <button
              type="button"
              className="btn-primary"
              onClick={handleSkip}
              disabled={isLoading}
            >
              {t("onboarding.inviteTeam.actions.skip")}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleSkip}
                disabled={isLoading}
              >
                {t("onboarding.inviteTeam.actions.skip")}
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isLoading || validRows.length === 0}
              >
                {isLoading
                  ? t("onboarding.inviteTeam.actions.creating")
                  : t("onboarding.inviteTeam.actions.invite", {
                      count: validRows.length,
                    })}
              </button>
            </>
          )}
        </div>
      </div>
    </form>
  );
};

export default Step3InviteTeam;
